//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useSelector } from 'react-redux';

import {
  RIGHTS_STATUS,
  RightsRequestCommunicationType,
} from '@apis/rights-management/rightsManagementApiModels';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import { RIGHTS_REQUEST_EXTERNAL_APPROVAL_COMMUNICATION_TYPES } from '@uia/rights/constants/uiaRightsConstants';
import {
  hasNoHistoricalUrls,
  useRightsHistoricalUrlsQuery,
} from '@uia/rights/hooks/useRightsHistoricalUrlsQuery';
import { useSyndicatedStatusQuery } from '@uia/rights/hooks/useSyndicatedStatusQuery';
import { getIsRightsRequestTermsAndConditionsEnabled } from '@uia/rights/selectors/getUIARights';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { getUrlWithoutProtocol } from '@utils/urlUtils';

import { termsConditionsSettings } from 'app/constants/rights-management/terms-and-conditions-settings';

const ALREADY_REQUESTED_STATUSES = [
  RIGHTS_STATUS.APPROVED,
  RIGHTS_STATUS.INITIATED,
  RIGHTS_STATUS.EXPIRED,
  RIGHTS_STATUS.PENDING,
];

export interface RequestRequiredForTCUrlParams {
  communicationType?: RightsRequestCommunicationType;
  clientId: ClientId;
  cuid: Cuid;
  rightsRequestText: string | undefined;
  status?: RIGHTS_STATUS;
}

export const useIsReRequestRequiredForMissingTCUrl = ({
  clientId,
  communicationType,
  cuid,
  rightsRequestText,
  status,
}: RequestRequiredForTCUrlParams) => {
  const isRightsRequestTermsAndConditionsEnabled = useSelector(
    getIsRightsRequestTermsAndConditionsEnabled,
  );
  const {
    data: syndicatedStatus,
    isError: isStatusError,
    isLoading: isStatusLoading,
  } = useSyndicatedStatusQuery(cuid, clientId, isDefined(status));
  const {
    data: isClientConfiguredUrlMissing,
    isError: isUrlsError,
    isLoading: isUrlsLoading,
  } = useRightsHistoricalUrlsQuery(hasNoHistoricalUrls, isDefined(status));

  const isError = isStatusError || isUrlsError;
  const isLoading = isStatusLoading || isUrlsLoading;

  if (isUndefined(status) || !syndicatedStatus?.isSyndicated) {
    return {
      isReRequestRequiredForMissingTCUrl: false,
      isError,
      isLoading,
    };
  }

  const hasExternalRightsApproval =
    isDefined(communicationType) &&
    RIGHTS_REQUEST_EXTERNAL_APPROVAL_COMMUNICATION_TYPES.includes(
      communicationType,
    ) &&
    status === RIGHTS_STATUS.APPROVED;
  const isAlreadyRequested = ALREADY_REQUESTED_STATUSES.includes(status);

  const termsAndConditionsUrls = Object.values<{ url: string }>(
    termsConditionsSettings,
  ).map(({ url }) => getUrlWithoutProtocol(url, true));

  const isMissingUrl =
    !isClientConfiguredUrlMissing &&
    !termsAndConditionsUrls.some((url) => rightsRequestText?.includes(url));

  const isReRequestRequiredForMissingTCUrl =
    isRightsRequestTermsAndConditionsEnabled &&
    !hasExternalRightsApproval &&
    isMissingUrl &&
    isAlreadyRequested;

  return {
    isReRequestRequiredForMissingTCUrl,
    isError,
    isLoading,
  };
};
